import { envelopeField, type MaeizEnvelope } from "@maeiz/core";
import { httpClient } from "@api/http";

export interface CreateDeliveryRequest {
    shiftId: string;
    customerId: string;
    productId: string;
    fullGiven: number;
    emptyReceived: number;
    cashCollected: number;
    clientTxnId: string;
    syncStatus?: string;
}

export interface DeliveryDto {
    id: string;
    shiftId: string;
    customerId: string;
    riderId: string;
    productId: string;
    fullGiven: number;
    emptyReceived: number;
    cashCollected: string;
    unitPriceApplied: string;
    clientTxnId: string | null;
    createdAt: string;
    product?: { id: string; name: string } | null;
}

export interface CreateDeliveryResult {
    deliveryId: string;
    synced: boolean;
    duplicate: boolean;
    delivery: DeliveryDto;
}

export interface SyncResultItem {
    index: number;
    clientTxnId: string;
    status: "ok" | "duplicate" | "error";
    deliveryId?: string;
    message?: string;
    errors?: Record<string, string[]>;
}

// The API's field names for delivery bodies are `full`/`empty`/`cash`, not case
// variants of fullGiven/emptyReceived/cashCollected — the global camelCase->snake_case
// body normalization on the backend can't bridge that, so we map explicitly here.
function toApiBody(d: CreateDeliveryRequest) {
    return {
        shift_id: d.shiftId,
        customer_id: d.customerId,
        product_id: d.productId,
        full: d.fullGiven,
        empty: d.emptyReceived,
        cash: d.cashCollected,
        client_txn_id: d.clientTxnId,
        sync_status: d.syncStatus,
    };
}

export const deliveriesApi = {
    create: async (data: CreateDeliveryRequest): Promise<CreateDeliveryResult> => {
        const res = (await httpClient.post<unknown>(
            "/rider/deliveries",
            toApiBody(data)
        )) as unknown as MaeizEnvelope;
        return {
            deliveryId: envelopeField<string>(res, "deliveryId", ""),
            synced: envelopeField<boolean>(res, "synced", true),
            duplicate: envelopeField<boolean>(res, "duplicate", false),
            delivery: envelopeField<DeliveryDto>(res, "delivery"),
        };
    },

    syncBatch: async (items: CreateDeliveryRequest[]): Promise<SyncResultItem[]> => {
        const res = (await httpClient.post<unknown>("/rider/sync/deliveries", {
            items: items.map(toApiBody),
        })) as unknown as MaeizEnvelope;
        return envelopeField<SyncResultItem[]>(res, "results", []) ?? [];
    },
};
